import ReactDOM from 'react-dom';
import { ReactEditor } from 'slate-react';
import styled from 'styled-components';
import { BaseRange, Editor } from 'slate';
import { SyntheticEvent } from 'react';
import i18n from 'src/utils/translate';
import { MyEditor } from 'src/components/textEditor/customHelpers';
import Avatar from 'src/components/avatar';
import { Mention } from 'src/utils/hooks/useFetchMentions';
type PortalProps = {
  addMention: (index: number, user: Mention) => void;
  editor?: Editor; // only pass if used from slate
  index?: number;
  isCanAdd?: boolean;
  setIndex?: (index: number) => void;
  target: Range | BaseRange;
  users: Mention[]; // mention suggestions
};
export default function MentionsPortal({
  users,
  index,
  isCanAdd,
  setIndex,
  editor,
  target,
  addMention
}: PortalProps): JSX.Element | null {
  let domRange;
  if (editor) {
    try {
      // try & catch needed because when numbers are added into mentions and passed through the fetchMentions hook,
      // the editor decides to reload itself (selection-wise), thus breaking the domRange
      // for that split second, we return null, until we can again get the editor text offsets & paths again
      domRange = ReactEditor.toDOMRange(editor, (target as BaseRange));
    } catch (ex) {
      return null;
    }
  } else {
    domRange = (target as Range);
  } // this is for simple editor passing in selection range

  const anchorRect = domRange.getBoundingClientRect();
  const onClick = (event: SyntheticEvent, index: number, user: Mention) => {
    event.preventDefault();
    addMention(index, user);
    if (editor) MyEditor.focus(editor);
  };
  const onHover = (index: number) => {
    if (setIndex) setIndex(index);
  };
  return typeof document === 'object' ? ReactDOM.createPortal(<PortalPanel data-testid="mentions-portal" style={{
    top: `${anchorRect?.top + window.pageYOffset + 24}px`,
    left: `${anchorRect?.left + window.pageXOffset}px`
  }}>
          {(editor ? !MyEditor.canAddMention(editor) : !isCanAdd) ? <Error>
              {i18n.t(`You've reached the maximum number of mentions`)}
            </Error> : users.map(({
      username,
      userId,
      userImageHash = ''
    }, i) => <PortalItem isSelection={i === index} key={userId} onMouseDown={e => onClick(e, i, {
      username: username,
      userId: userId
    })} onMouseOver={() => onHover(i)}>
                <Avatar alt={`${username} avatar`} hash={userImageHash} />
                <div> {username} </div>
              </PortalItem>)}
        </PortalPanel>, document.body) : null;
}
const PortalPanel = styled.div`
  position: absolute;
  z-index: 100;
  padding: 3px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0,0,0,.2);
`;
const PortalItem = styled.div<{
  isSelection?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 3px 5px;
  border-radius: 3px;
  background: ${props => props.isSelection ? props.theme.colorGreyLight : 'transparent'};
`;
const Error = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  border-radius: 3px;
  background: ${({
  theme
}) => theme.colorYellowLight};
`;